import React, { useState, useRef, useEffect } from 'react';
import { ArrowUpRight, Shield, BarChart4, Search, Sliders, TrendingUp, Users, ChevronLeft, ChevronRight, PieChart, Coins, Clock, Target, BarChart3, LineChart, Filter, Layers, Brain, Database, TrendingDown } from 'lucide-react';

const CoralFactorServices = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Minimum swipe distance for mobile
  const minSwipeDistance = 50;
  
  const services = [
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Multi-Factor ETF Allocation",
      description: "Our systematic approach constructs diversified portfolios across proven investment factors including value, momentum, quality, and low volatility, utilizing quantitative models to optimize factor exposure and capture factor premiums.",
      accent: "bg-[#F49611]",
      gradient: "from-[#F49611]/20 to-[#F6A839]/20",
      borderColor: "border-[#F9C275]",
      hoverBorder: "hover:border-[#F49611]",
      iconColor: "text-[#F49611]",
      hoverBg: "hover:bg-[#FEF5E7]"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Quantitative Signal Processing",
      description: "CORAL Factors leverages advanced quantitative models and data-driven signals to identify optimal factor timing and allocation, processing multiple data sources to enhance factor selection and portfolio construction decisions.",
      accent: "bg-[#1C52A0]",
      gradient: "from-[#1C52A0]/20 to-[#436FB0]/20",
      borderColor: "border-[#7B9BC8]",
      hoverBorder: "hover:border-[#1C52A0]",
      iconColor: "text-[#1C52A0]",
      hoverBg: "hover:bg-[#E8EEF6]"
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Risk Management Protocols",
      description: "Our strategy employs sophisticated risk management frameworks with portfolio optimization techniques, volatility targeting, and correlation analysis to maintain optimal risk-adjusted returns across different market cycles.",
      accent: "bg-[#F49611]",
      gradient: "from-[#F49611]/20 to-[#F6A839]/20",
      borderColor: "border-[#F9C275]",
      hoverBorder: "hover:border-[#F49611]",
      iconColor: "text-[#F49611]",
      hoverBg: "hover:bg-[#FEF5E7]"
    },
    {
      icon: <TrendingDown className="w-6 h-6" />,
      title: "Factor Premium Capture",
      description: "CORAL Factors systematically captures factor premiums through disciplined rebalancing and strategic factor timing, with backtested results demonstrating consistent alpha generation and superior Sharpe ratios compared to traditional indexing.",
      accent: "bg-[#1C52A0]",
      gradient: "from-[#1C52A0]/20 to-[#436FB0]/20",
      borderColor: "border-[#7B9BC8]",
      hoverBorder: "hover:border-[#1C52A0]",
      iconColor: "text-[#1C52A0]",
      hoverBg: "hover:bg-[#E8EEF6]"
    }
  ];

  // Handle touch events for swipe functionality
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && activeIndex < services.length - 1) {
      setActiveIndex(current => current + 1);
    }
    
    if (isRightSwipe && activeIndex > 0) {
      setActiveIndex(current => current - 1);
    }
  };

  // Navigate to previous or next service
  const handleNext = () => {
    if (activeIndex < services.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const handlePrev = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  return (
    <div className="py-12 relative overflow-hidden">
      <div className="relative max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        <div className="mb-16">
          <div className="relative mb-16 text-center lg:text-left">
            <div className="inline-block">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
                Coral Factor Services
              </h2>
              <div className="absolute -right-8 top-0 w-16 h-16 bg-[#F49611]/10 rounded-full blur-xl animate-ping" />
            </div>
            <p className="text-xl text-neutral-600 mt-4 max-w-2xl mx-auto lg:mx-0">
              Systematic Factor Investing Through Advanced Quantitative Models
            </p>
          </div>
        </div>

        {/* Mobile and Tablet View - Card Carousel */}
        <div 
          className="lg:hidden relative"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {services.map((service, idx) => (
                <div key={idx} className="w-full flex-shrink-0 px-1">
                  <div className={`bg-white rounded-2xl p-6 sm:p-8 shadow-lg h-full border-2 ${service.borderColor}`}>
                    <div className={`w-14 h-14 rounded-xl ${service.accent} bg-opacity-20 flex items-center 
                      justify-center mb-5 ${service.iconColor}`}>
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold text-[#113262] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-sm sm:text-base text-neutral-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-between items-center mt-6">
            <div className="flex gap-2">
              {services.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    activeIndex === idx ? `w-6 ${services[activeIndex].iconColor}` : 'bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={handlePrev}
                disabled={activeIndex === 0}
                className={`p-2 rounded-full ${
                  activeIndex === 0 
                    ? 'bg-gray-100 cursor-not-allowed' 
                    : 'bg-gray-200 cursor-pointer'
                }`}
                aria-label="Previous service"
              >
                <ChevronLeft size={20} className="text-gray-700" />
              </button>
              
              <button 
                onClick={handleNext}
                disabled={activeIndex === services.length - 1}
                className={`p-2 rounded-full ${
                  activeIndex === services.length - 1 
                    ? 'bg-gray-100 cursor-not-allowed' 
                    : 'bg-gray-200 cursor-pointer'
                }`}
                aria-label="Next service"
              >
                <ChevronRight size={20} className="text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Layout - Original */}
        <div className="hidden lg:flex lg:flex-row gap-8">
          {/* Service Navigation */}
          <div className="lg:w-1/3">
            <div className="sticky top-8 space-y-4">
              {services.map((service, idx) => (
                <div
                  key={idx}
                  className={`group cursor-pointer transition-all duration-500 transform ${
                    activeIndex === idx 
                      ? `bg-white shadow-lg scale-105 border-2 ${service.borderColor}` 
                      : `hover:scale-102 border ${service.borderColor} ${service.hoverBorder} ${service.hoverBg}`
                  } rounded-xl backdrop-blur-sm`}
                  onClick={() => setActiveIndex(idx)}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="p-6 flex items-center gap-4 relative overflow-hidden">
                    <div className={`w-12 h-12 rounded-lg ${service.accent} bg-opacity-20 flex items-center justify-center
                      transition-all duration-300 ${hoveredIndex === idx ? 'scale-110' : ''} ${service.iconColor} 
                      group-hover:bg-opacity-30`}>
                      {service.icon}
                    </div>
                    <h3 className={`font-semibold transition-colors duration-300 ${
                      activeIndex === idx ? service.iconColor : 'text-neutral-700'
                    }`}>
                      {service.title}
                    </h3>
                    <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} 
                      transition-opacity duration-300 opacity-0 ${hoveredIndex === idx ? 'opacity-100' : ''}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Service Details */}
          <div className="lg:w-2/3">
            <div className="relative min-h-[400px]">
              {services.map((service, idx) => (
                <div
                  key={idx}
                  className={`transition-all duration-700 absolute w-full ${
                    activeIndex === idx 
                      ? 'opacity-100 translate-x-0 translate-y-0 rotate-0'
                      : idx < activeIndex
                        ? 'opacity-0 -translate-x-8 -translate-y-4 -rotate-6'
                        : 'opacity-0 translate-x-8 translate-y-4 rotate-6'
                  }`}
                  style={{ zIndex: activeIndex === idx ? 10 : 0 }}
                >
                  <div className={`bg-white/80 backdrop-blur-lg rounded-2xl p-8 shadow-lg
                    transition-all duration-500 hover:scale-[1.02] border-2 ${service.borderColor}`}>
                    <div className={`w-16 h-16 rounded-xl ${service.accent} bg-opacity-20 flex items-center justify-center mb-6
                      transform transition-transform duration-500 hover:rotate-12 ${service.iconColor}`}>
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-[#113262] mb-4">
                      {service.title}
                    </h3>
                    <p className="text-lg text-neutral-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoralFactorServices;